import React, { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import s from "./map-controls.module.scss";
import { useAppSelector } from "../../hooks/useAppSelector";
import Wheel from "./wheel/wheel";
import { Layer } from "../../interfaces/layer";
import { useDebounceLayers } from "./useDebounceLayers";
import { useAppDispatch } from "../../hooks/useAppDispatch";
import {
  polylineOff,
  polylineOn,
  zoomIn,
  zoomOut,
} from "../../services/actions/map";
import {
  InfrastructureTypeIds,
  LayersDataTypes,
} from "../../types/layers-data-types";
import InfrastructureButton from "./infrastructure-button/infrastructure-button";
import LayersMenu from "./layers-menu/layers-menu";
import {
  getLayers,
  MapSection,
  setZoom,
  startDrawingTools,
  stopDrawingTools,
} from "../../services/reducers/map";
import ImportFileModalMapControls from "./import-file-modal-map-controls";

const MapControls = () => {
  const { t } = useTranslation();
  const [layersMenuActive, setLayersMenuActive] = useState<boolean>(false);
  const [rulerIsActive, setRulerIsActive] = useState<boolean>(false);

  const [isModalOpen, setIsModalOpen] = useState(false);

  const openModal = () => {
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  const {
    mapMode,
    scale,
    zoom,
    visibleLayersId,
    section,
    enableDrawingTools,
    drawingToolsIsActive,
  } = useAppSelector((state) => state.map);
  const { user } = useAppSelector((state) => state.user);

  const is3D = mapMode === "3d";

  const dispatch = useAppDispatch();
  const layers = useAppSelector(getLayers);

  const {
    infrastructures,
    radioLayers,
    modelCheckboxesLayers,
    checkboxesLayers,
  } = useMemo(() => {
    const infrastructures: Layer[] = [];
    const radioLayers: Layer[] = [];
    const modelCheckboxesLayers: Layer[] = [];
    const checkboxesLayers: Layer[] = [];

    for (const layer of layers) {
      if (InfrastructureTypeIds.includes(layer.dataType)) {
        infrastructures.push(layer);
      }
      if (!layer.parentId && !layer.hidden) {
        if (layer.dataType === LayersDataTypes.BaseMap) {
          radioLayers.push(layer);
        } else if (
          layer.dataType === LayersDataTypes.Map2D ||
          layer.dataType === LayersDataTypes.Map3D
        ) {
          if (!is3D && layer.dataType === LayersDataTypes.Map3D) continue;
          modelCheckboxesLayers.push(layer);
        } else {
          checkboxesLayers.push(layer);
        }
      }
    }

    return {
      infrastructures,
      radioLayers,
      checkboxesLayers,
      modelCheckboxesLayers,
    };
  }, [layers, is3D]);

  const debounce = useCallback(useDebounceLayers(visibleLayersId), [
    useDebounceLayers,
    visibleLayersId,
  ]);

  const formatScale = (scale: number) => {
    return scale.toString().replace(/\B(?=(\d{3})+(?!\d))/g, " ");
  };

  const handleSwitchRulerActive = () => {
    if (!rulerIsActive && drawingToolsIsActive) {
      dispatch(stopDrawingTools());
    }

    setRulerIsActive((prev) => !prev);
    dispatch(!rulerIsActive ? polylineOn() : polylineOff());
  };

  const handleToggleDrawingTools = () => {
    if (!drawingToolsIsActive && rulerIsActive) {
      dispatch(polylineOff());
      setRulerIsActive(false);
    }

    dispatch(drawingToolsIsActive ? stopDrawingTools() : startDrawingTools());
  };

  const allSelected = useAppSelector((store) => store.map.visibleLayersId);
  return (
    <div className={`${s.mapControls} ${is3D && s.mapControls3DMode}`}>
      {section === MapSection.search && (
        <InfrastructureButton
          infrastructures={infrastructures}
          debounce={debounce}
          allSelected={allSelected}
        />
      )}
      {layersMenuActive && (
        <LayersMenu
          is3D={is3D}
          setLayersMenuActive={setLayersMenuActive}
          radioLayers={radioLayers}
          checkboxesLayers={checkboxesLayers}
          modelCheckboxesLayers={modelCheckboxesLayers}
          debounce={debounce}
          allSelected={allSelected}
        />
      )}
      <div className={s.mainControlsContainer}>
        {
          <button
            className={`${s.openLayersMenuButton} ${s.element} ${layersMenuActive && s.active}`}
            onClick={() => setLayersMenuActive((prev) => !prev)}
          ></button>
        }
        <button
          id="startDrawingButton"
          className={`${s.rulerButton} ${s.element} ${rulerIsActive && s.active}`}
          onClick={handleSwitchRulerActive}
        />
        {user && (
          <button
            disabled={!enableDrawingTools}
            className={`${s.drawingToolButton} ${s.element} ${drawingToolsIsActive ? s.active : ""}`}
            onClick={handleToggleDrawingTools}
          >
            <div className={s.drawingToolIcon}></div>
          </button>
        )}
        <>
          <button
            className={`${s.zoomInButton} ${s.element}`}
            id="zoomInButton"
            onClick={() => {
              dispatch(zoomIn());
              dispatch(setZoom({ zoom: zoom + 1, scale }));
            }}
          ></button>
          <button
            className={`${s.zoomOutButton} ${s.element}`}
            id="zoomOutButton"
            onClick={() => {
              dispatch(zoomOut());
              dispatch(setZoom({ zoom: zoom - 1, scale }));
            }}
          ></button>
        </>
        {(section === MapSection.search ||
          section === MapSection.utilities) && <Wheel is3D={is3D} />}
        {!is3D && (
          <div className={`${s.zoomLVL} ${s.element}`}>
            1 : {scale.toString().length > 6 ? <br /> : null}
            {formatScale(scale)}
          </div>
        )}
        {(section === MapSection.search || section === MapSection.masterPlan) &&
          user && (
            <>
              <button
                className={`${s.importFileButton} ${s.element} ${isModalOpen && s.active}`}
                onClick={openModal}
              >
                <div className={s.importIcon}></div>
                {t("map-controls.importFile")}
              </button>
              {isModalOpen && (
                <ImportFileModalMapControls onClose={closeModal} />
              )}
            </>
          )}
      </div>
    </div>
  );
};

export default MapControls;
